// Pure helpers of the upload pipeline (GED-FE-02): the part layout the server uses, the client-side
// pre-check, plain-language issues for every refusal or failure, size formatting and the files
// of a dropped folder.
import { ApiError } from '@batiflow/client-core';
import {
  MAX_UPLOAD_BYTES,
  UploadFilenameSchema,
  allowedUpload,
  extensionOf,
  type UploadMetadata,
  type UploadRejectionReason,
} from '@batiflow/contracts';
import { intlLocale, type AppLocale } from '@batiflow/i18n';

// --- Part layout (the server's `partPlan`, apps/api upload-rules.ts: 8 MiB parts, at most 10 000).

const MIB = 1024 ** 2;
const MIN_PART_BYTES = 8 * MIB;
const MAX_PARTS = 10_000;

/**
 * Bytes per part of an upload of `size` bytes. The server fixes the layout from the size alone, so
 * Uppy can cut the file the same way before the upload exists; the answer of the server is checked
 * against it.
 */
export function partSizeFor(size: number): number {
  return Math.max(MIN_PART_BYTES, Math.ceil(size / MAX_PARTS / MIB) * MIB);
}

// --- Rows.

/** `refused`: nothing to retry (the file or the folder is the problem). `failed`: try again. */
export type UploadPhase = 'sending' | 'scanning' | 'added' | 'refused' | 'failed';

export type UploadIssueCode =
  | 'empty'
  | 'name'
  | 'type'
  | 'tooLarge'
  | 'tooLargePlan'
  | 'quota'
  | 'forbidden'
  | 'notFound'
  | 'readOnly'
  | 'locked'
  | 'expired'
  | 'incomplete'
  | 'busy'
  | 'interrupted'
  | 'statusLost'
  | 'unexpected'
  | UploadRejectionReason;

export interface UploadIssue {
  code: UploadIssueCode;
  /** The extension of the refused file (`type`), null when it has none. */
  extension?: string | null;
}

export interface UploadTarget {
  spaceId: string;
  folderId: string;
  /** A new version of this document (the file also carries its `revisionReason`). */
  documentId?: string;
}

/**
 * What a file carries into its create call besides the file itself: the metadata of a new
 * document, or the reason of a new version of `target.documentId`.
 */
export interface UploadExtras {
  metadata?: UploadMetadata;
  revisionReason?: string;
}

export interface UploadRow {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  spaceId: string;
  folderId: string;
  phase: UploadPhase;
  /** 0–100 while sending. */
  percent: number;
  issue: UploadIssue | null;
  /** The check takes longer than usual. */
  slow: boolean;
  /** `failed` rows only: a new attempt makes sense. */
  retryable: boolean;
  documentId: string | null;
  versionId: string | null;
}

export const isActive = (row: UploadRow): boolean =>
  row.phase === 'sending' || row.phase === 'scanning';

// --- Pre-check, before any request.

/** The reason a file is refused at once, or null when it may be sent. */
export function precheck(file: Pick<File, 'name' | 'size' | 'type'>): UploadIssue | null {
  if (file.size < 1) return { code: 'empty' };
  if (!UploadFilenameSchema.safeParse(file.name).success) return { code: 'name' };
  if (!allowedUpload(file.name, file.type))
    return { code: 'type', extension: extensionOf(file.name) };
  if (file.size > MAX_UPLOAD_BYTES) return { code: 'tooLarge' };
  return null;
}

/** Files nobody means to send: the operating system adds them to folders. */
const JUNK_FILES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);
export const isJunkFile = (name: string): boolean => JUNK_FILES.has(name.toLowerCase());

// --- Server answers → plain-language issues.

export interface Classified {
  phase: 'refused' | 'failed';
  issue: UploadIssue;
  retryable: boolean;
}

const refused = (code: UploadIssueCode): Classified => ({
  phase: 'refused',
  issue: { code },
  retryable: false,
});
const failed = (code: UploadIssueCode): Classified => ({
  phase: 'failed',
  issue: { code },
  retryable: true,
});

/** The HTTP status Uppy attaches to a failed part upload (`error.source`), when there is one. */
const partStatusOf = (error: unknown): number | null => {
  const source = (error as { source?: { status?: unknown } } | null)?.source;
  return typeof source?.status === 'number' ? source.status : null;
};

/** What an upload error means for the person: refused for good, or worth another attempt. */
export function classifyError(error: unknown): Classified {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'FILE_TYPE_NOT_ALLOWED':
        return refused('type');
      case 'FILE_TOO_LARGE':
        return refused('tooLargePlan');
      case 'QUOTA_EXCEEDED':
        return refused('quota');
      case 'FORBIDDEN':
        return refused('forbidden');
      case 'NOT_FOUND':
        return refused('notFound');
      case 'ORGANIZATION_READ_ONLY':
        return refused('readOnly');
      case 'DOCUMENT_LOCKED':
        return refused('locked');
      case 'UPLOAD_EXPIRED':
        return failed('expired');
      case 'RATE_LIMITED':
        return failed('busy');
      case 'VALIDATION_FAILED': {
        const detail = error.fieldErrors[0]?.code;
        return detail === 'UPLOAD_PARTS_INVALID' || detail === 'UPLOAD_SIZE_MISMATCH'
          ? failed('incomplete')
          : failed('unexpected');
      }
      default:
        return error.status >= 500 ? failed('interrupted') : failed('unexpected');
    }
  }
  // A failed part (the files origin answered, or the connection dropped): Uppy already retried.
  if (partStatusOf(error) !== null || error instanceof TypeError) return failed('interrupted');
  return failed('unexpected');
}

/** The files worker refused the file after the scan: nothing to retry, the file is the problem. */
export const classifyRejection = (reason: UploadRejectionReason | null): Classified =>
  refused(reason ?? 'unexpected');

export const statusLost = (): Classified => ({
  phase: 'failed',
  issue: { code: 'statusLost' },
  retryable: false,
});

// --- Formatting (display only).

const SIZE_UNITS = [
  { unit: 'gigabyte', factor: 1024 ** 3 },
  { unit: 'megabyte', factor: 1024 ** 2 },
  { unit: 'kilobyte', factor: 1024 },
  { unit: 'byte', factor: 1 },
] as const;

/** "1,5 Mo" (fr), "1.5 MB" (en): a file size in the language of the interface. */
export function formatBytes(bytes: number, locale: AppLocale): string {
  const { unit, factor } = SIZE_UNITS.find((candidate) => bytes >= candidate.factor) ?? {
    unit: 'byte',
    factor: 1,
  };
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: unit === 'byte' ? 0 : 1,
  }).format(bytes / factor);
}

/** "42 %" (fr), "42%" (en). */
export function formatPercent(percent: number, locale: AppLocale): string {
  return new Intl.NumberFormat(intlLocale(locale), { style: 'percent' }).format(percent / 100);
}

// --- Dropped folders.

/** The most files taken from one drop (a whole drive dropped by mistake). */
export const MAX_DROPPED_FILES = 2000;
const MAX_FOLDER_DEPTH = 24;

const fileOf = (entry: FileSystemFileEntry): Promise<File | null> =>
  new Promise((resolve) => {
    entry.file(resolve, () => {
      resolve(null);
    });
  });

const childrenOf = (directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> =>
  new Promise((resolve) => {
    const reader = directory.createReader();
    const all: FileSystemEntry[] = [];
    // readEntries returns its results in batches (100 in Chromium): read until an empty batch.
    const next = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) resolve(all);
          else {
            all.push(...batch);
            next();
          }
        },
        () => {
          resolve(all);
        },
      );
    };
    next();
  });

async function walk(entry: FileSystemEntry, depth: number, out: File[]): Promise<void> {
  if (out.length >= MAX_DROPPED_FILES || depth > MAX_FOLDER_DEPTH) return;
  if (entry.isFile) {
    const file = await fileOf(entry as FileSystemFileEntry);
    if (file && !isJunkFile(file.name)) out.push(file);
  } else if (entry.isDirectory) {
    for (const child of await childrenOf(entry as FileSystemDirectoryEntry))
      await walk(child, depth + 1, out);
  }
}

/**
 * The files of a drop, folders included: each folder is read down to its files and the structure
 * is flattened (every file goes into the open folder). The entries are read at once, before the
 * first await: the browser empties the drop when the handler returns.
 */
export async function collectDroppedFiles(data: DataTransfer): Promise<File[]> {
  const picked = [...data.items]
    .filter((item) => item.kind === 'file')
    .map((item) => ({
      entry: typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null,
      file: item.getAsFile(),
    }));
  const files: File[] = [];
  for (const { entry, file } of picked) {
    if (entry) await walk(entry, 0, files);
    else if (file && !isJunkFile(file.name)) files.push(file);
  }
  if (picked.length === 0) {
    // Browsers without `items`: the plain list (folders cannot be read this way).
    for (const file of data.files) if (!isJunkFile(file.name)) files.push(file);
  }
  return files.slice(0, MAX_DROPPED_FILES);
}
